import { NavLink, useParams } from 'react-router-dom';
import type { ComponentType } from 'react';
import {
  LineChart,
  Building2,
  Home,
  Users,
  Receipt,
  Landmark,
  Gavel,
  FileClock,
  Cog,
  Wallet,
  Store,
  MessageCircle,
  Package,
  QrCode,
  ShieldCheck,
  BarChart2,
} from 'lucide-react';
import type { Rol, FeatureKey } from '@condo/contracts';
import { isFeatureEnabled } from '@condo/contracts';
import { useAuth } from '@/features/auth/use-auth';
import { useFeatureFlags } from '@/features/feature-flags/api';
import { useMyTenants } from '@/features/me/api';
import { cn } from '@/lib/utils/cn';

interface NavItem {
  to: string;
  labelKey: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  allow: readonly Rol[];
  section: 'main' | 'finance' | 'legal' | 'mercado' | 'foot';
  /** When true, NavLink only matches the exact path (not child routes). */
  end?: boolean;
  /** Feature flag key that must be enabled for this item to appear (e.g. 'modules.paquetes'). */
  featureFlag?: FeatureKey;
}

/**
 * Builds the tenant-scoped navigation items with paths relative to condoId.
 */
function makeTenantNav(condoId: string): readonly NavItem[] {
  const base = `/app/condominios/${condoId}`;
  return [
    {
      to: `${base}`,
      labelKey: 'dashboard',
      label: 'Dashboard',
      icon: LineChart,
      allow: ['super_admin', 'admin', 'comite', 'propietario', 'residente'],
      section: 'main',
      end: true,
    },
    {
      to: `${base}/administradores`,
      labelKey: 'administradores',
      label: 'Administradores',
      icon: ShieldCheck,
      allow: ['super_admin'],
      section: 'main',
    },
    {
      to: `${base}/unidades`,
      labelKey: 'unidades',
      label: 'Unidades',
      icon: Home,
      allow: ['admin', 'comite'],
      section: 'main',
    },
    {
      to: `${base}/residentes`,
      labelKey: 'residentes',
      label: 'Residentes',
      icon: Users,
      allow: ['admin', 'comite'],
      section: 'main',
    },
    {
      to: `${base}/gastos-comunes`,
      labelKey: 'gastosComunes',
      label: 'Gastos comunes',
      icon: Receipt,
      allow: ['admin', 'comite'],
      section: 'finance',
    },
    {
      to: `${base}/fondo-reserva`,
      labelKey: 'fondoReserva',
      label: 'Fondo de reserva',
      icon: Landmark,
      allow: ['admin', 'comite', 'propietario', 'residente'],
      section: 'finance',
    },
    {
      to: `${base}/mis-cargos`,
      labelKey: 'misCargos',
      label: 'Mis cargos',
      icon: Wallet,
      allow: ['residente'],
      section: 'finance',
    },
    {
      to: `${base}/visitas`,
      labelKey: 'visitas',
      label: 'Visitas',
      icon: QrCode,
      allow: ['admin', 'conserje', 'residente'],
      section: 'main',
      end: true,
    },
    {
      to: `${base}/paquetes`,
      labelKey: 'paquetes',
      label: 'Paquetes',
      icon: Package,
      allow: ['super_admin', 'admin', 'conserje', 'comite', 'propietario', 'residente'],
      section: 'main',
      end: true,
      featureFlag: 'modules.paquetes',
    },
    {
      to: `${base}/mercado`,
      labelKey: 'mercado',
      label: 'Mercado Vecinal',
      icon: Store,
      allow: ['super_admin', 'admin', 'comite', 'conserje', 'residente'],
      section: 'mercado',
      end: true,
    },
    {
      to: `${base}/mercado/chats`,
      labelKey: 'mercadoChats',
      label: 'Mensajes',
      icon: MessageCircle,
      allow: ['residente'],
      section: 'mercado',
    },
    {
      to: `${base}/mercado/mis-publicaciones`,
      labelKey: 'mercadoMisAvisos',
      label: 'Mis avisos',
      icon: Package,
      allow: ['residente'],
      section: 'mercado',
    },
    {
      to: `${base}/asambleas`,
      labelKey: 'asambleas',
      label: 'Asambleas',
      icon: Gavel,
      allow: ['admin', 'comite', 'propietario', 'residente'],
      section: 'legal',
    },
    {
      to: `${base}/audit-log`,
      labelKey: 'auditLog',
      label: 'Historial',
      icon: FileClock,
      allow: ['admin', 'comite'],
      section: 'legal',
    },
  ];
}

/** Platform-level nav (super_admin only, no condoId required). */
const PLATFORM_NAV: readonly NavItem[] = [
  {
    to: '/app/platform',
    labelKey: 'condominios',
    label: 'Condominios',
    icon: Building2,
    allow: ['super_admin'],
    section: 'main',
    end: true,
  },
  {
    to: '/app/platform/metricas',
    labelKey: 'metricas',
    label: 'Métricas (próximamente)',
    icon: BarChart2,
    allow: ['super_admin'],
    section: 'main',
  },
];

const SECTIONS: { key: NavItem['section']; label: string }[] = [
  { key: 'main', label: 'Panel' },
  { key: 'finance', label: 'Finanzas' },
  { key: 'legal', label: 'Gobernanza' },
  { key: 'mercado', label: 'Mercado' },
];

const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Super administrador',
  admin: 'Administradora',
  comite: 'Comité',
  conserje: 'Conserjería',
  propietario: 'Propietario',
  residente: 'Residente',
};

interface SidebarProps {
  platformMode?: boolean;
}

export function Sidebar({ platformMode = false }: SidebarProps) {
  const { session } = useAuth();
  // condoId is present when inside /app/condominios/:condoId/* routes.
  const { condoId } = useParams<{ condoId?: string }>();
  const { data: featureFlags } = useFeatureFlags(condoId);
  const { data: tenantsData } = useMyTenants();
  const activeTenant = tenantsData?.items.find((t) => t.tenantId === condoId);

  if (!session) return null;

  let rol: Rol | undefined;
  if (session.isSuperAdmin) {
    rol = 'super_admin';
  } else if (condoId) {
    rol = session.tenants[condoId] as Rol | undefined;
  }
  if (!rol) return null;

  const allNav: readonly NavItem[] = platformMode
    ? PLATFORM_NAV
    : condoId
      ? makeTenantNav(condoId)
      : [];

  // Hide items whose featureFlag is explicitly disabled for this tenant.
  const nav = allNav.filter((item) => {
    if (!item.featureFlag) return true;
    if (!featureFlags) return true; // show while loading (avoid flicker)
    return isFeatureEnabled(featureFlags, item.featureFlag);
  });

  return (
    <aside
      className="sticky top-0 z-30 hidden h-screen w-64 shrink-0 flex-col border-r border-white/10 bg-[var(--color-ink)] lg:flex"
      aria-label="Navegacion principal"
    >
      {/* Context chip */}
      <div className="mx-4 mt-6 rounded-md border border-white/10 bg-white/5 px-4 py-3">
        {platformMode ? (
          <>
            <p className="text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-white/40">
              Vista plataforma
            </p>
            <p className="mt-0.5 text-sm font-semibold text-white/90 truncate">
              Super administrador
            </p>
          </>
        ) : (
          <>
            <p className="text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-white/40">
              Condominio activo
            </p>
            {activeTenant ? (
              <>
                <p className="mt-0.5 text-sm font-semibold text-white/90 truncate">
                  {activeTenant.nombre}
                </p>
                <span className="mt-2 inline-block rounded border border-white/15 bg-white/10 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-white/60">
                  {ROLE_LABEL[activeTenant.rol] ?? activeTenant.rol}
                </span>
              </>
            ) : (
              <div className="mt-1.5 h-4 w-32 animate-pulse rounded bg-white/10" />
            )}
          </>
        )}
      </div>

      {/* Nav sections */}
      <nav className="mt-6 flex-1 space-y-6 overflow-y-auto px-4 pb-6">
        {SECTIONS.map((section) => {
          const items = nav.filter(
            (i) => i.section === section.key && i.allow.includes(rol!),
          );
          if (items.length === 0) return null;
          return (
            <div key={section.key}>
              <p
                className="eyebrow mb-2 px-3"
                style={{ color: 'rgba(255,255,255,0.35)' }}
              >
                {section.label}
              </p>
              <ul className="space-y-0.5">
                {items.map(({ to, label, icon: Icon, end }) => (
                  <li key={to}>
                    <NavLink
                      to={to}
                      end={end}
                      className={({ isActive }) =>
                        cn(
                          'relative flex items-center gap-3 py-2 pl-4 pr-3 text-[0.8125rem] transition-colors duration-150 ease-out',
                          isActive
                            ? 'font-semibold text-[var(--color-primary-50)] before:absolute before:left-0 before:top-1/2 before:h-5 before:w-[2px] before:-translate-y-1/2 before:bg-[var(--color-primary-50)] before:content-[""]'
                            : 'text-white/50 hover:text-white/80',
                        )
                      }
                    >
                      <Icon className="h-[18px] w-[18px] shrink-0" />
                      <span>{label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* Legal footer */}
      <div className="border-t border-white/10 px-6 py-4">
        {condoId && !platformMode && (
          <NavLink
            to={`/app/condominios/${condoId}/settings`}
            className="flex items-center gap-2 text-xs text-white/40 hover:text-white/70"
          >
            <Cog className="h-3.5 w-3.5" />
            Configuracion
          </NavLink>
        )}
        <p className="mt-3 text-[0.625rem] leading-relaxed text-white/30">
          Alineado con la{' '}
          <span className="font-semibold text-[var(--color-accent)]">
            Ley 21.442
          </span>{' '}
          de Copropiedad Inmobiliaria.
        </p>
      </div>
    </aside>
  );
}
